import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CHALLENGE_ACTIONS, DEFAULT_PASS_SIZE, PASS_OFFERS, PASS_SIZES, type PassSize } from '@sss/types';
import HumanCheck from '../components/Secret/HumanCheck';
import secretStyles from '../components/Secret/Secret.module.css';
import { useHumanCheck } from '../hooks/useHumanCheck';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePass } from '../hooks/usePass';
import { createPassCheckout, forgetPendingTopUp, savePendingPass, savePendingTopUp } from '../lib/relayApi';
import { SecretsApiError } from '../lib/secretsApi';
import ui from '../styles/ui.module.css';
import buyStyles from './BuyPassPage.module.css';
import styles from './RevealPage.module.css';

/**
 * /pass/buy — buy a relay pass (docs/adr/0013). Opened in its own tab from
 * the file form, so the transfer waiting there survives. After the human
 * check, the Worker returns Stripe's Checkout page and the pass token; the
 * token waits in this browser (sss.passPending) until /pass/bought sees the
 * payment arrive. No Stripe script ever runs on our pages.
 */
export default function BuyPassPage() {
  const { t } = useTranslation();
  usePageTitle(t('pageTitle.buyPass'));
  const humanCheck = useHumanCheck(CHALLENGE_ACTIONS.buyPass);
  const [size, setSize] = useState<PassSize>(DEFAULT_PASS_SIZE);
  // With a pass already in this browser, the default is to add to it: one balance (docs/adr/0015).
  const [current] = usePass();
  const [addToCurrent, setAddToCurrent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const cancelled = new URLSearchParams(window.location.search).has('cancelled');

  async function onContinue() {
    const turnstileToken = humanCheck.take();
    if (!turnstileToken) {
      setProblem(t('pass.buy.checkFirst'));
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const topUp = current && addToCurrent ? current : undefined;
      const { url, pass, checkout } = await createPassCheckout({ turnstileToken, size, topUp });
      savePendingPass(pass);
      if (topUp) savePendingTopUp(checkout);
      else forgetPendingTopUp();
      window.location.assign(url);
    } catch (err) {
      const type = err instanceof SecretsApiError ? err.type : 'other';
      setProblem(t(type === 'challengeFailed' || type === 'rateLimited' || type === 'network' ? `pass.buy.problems.${type}` : 'pass.buy.problems.other'));
      setBusy(false);
    }
  }

  return (
    <main className={styles.page}>
      <div className={styles.stack}>
        <p className={ui.eyebrow}>{t('pass.eyebrow')}</p>
        <h1 className={styles.title}>{t('pass.buy.title')}</h1>
        <p className={styles.lead}>{t('pass.buy.lead')}</p>
        <fieldset className={buyStyles.sizes}>
          <legend className={buyStyles.legend}>{t('pass.buy.sizeLegend')}</legend>
          {PASS_SIZES.map((s) => {
            const offer = PASS_OFFERS[s];
            const gb = offer.bytes / 1024 ** 3;
            return (
              <label key={s} className={`${buyStyles.size} ${size === s ? buyStyles.sizeOn : ''}`}>
                <input type="radio" name="pass-size" value={s} checked={size === s} onChange={() => setSize(s)} className={buyStyles.radio} />
                <span className={buyStyles.gb}>{t('pass.buy.gb', { gb })}</span>
                <span className={buyStyles.days}>{t('pass.buy.days', { days: offer.days })}</span>
                <span className={buyStyles.price}>{t('pass.buy.amount', { price: offer.price })}</span>
                <span className={buyStyles.perGb}>{t('pass.buy.perGb', { per: (offer.price / gb).toFixed(2) })}</span>
              </label>
            );
          })}
        </fieldset>
        {current && (
          <fieldset className={buyStyles.target}>
            <legend className={buyStyles.legend}>{t('pass.buy.targetLegend')}</legend>
            <label className={buyStyles.targetOption}>
              <input type="radio" name="pass-target" checked={addToCurrent} onChange={() => setAddToCurrent(true)} />
              <span>{t('pass.buy.addToCurrent')}</span>
            </label>
            <label className={buyStyles.targetOption}>
              <input type="radio" name="pass-target" checked={!addToCurrent} onChange={() => setAddToCurrent(false)} />
              <span>{t('pass.buy.separate')}</span>
            </label>
          </fieldset>
        )}
        <ul className={ui.prose}>
          <li>{t('pass.buy.price')}</li>
          <li>{t('pass.buy.noAccount')}</li>
          <li>{t('pass.buy.stripe')}</li>
        </ul>
        {cancelled && (
          <p className={secretStyles.notice} role="status">
            {t('pass.buy.cancelled')}
          </p>
        )}
        <HumanCheck check={humanCheck} />
        <div className={secretStyles.actions}>
          <button type="button" className={ui.button} onClick={() => void onContinue()} disabled={busy || humanCheck.state !== 'ready'}>
            {busy ? t('pass.buy.opening') : t('pass.buy.continue')}
          </button>
        </div>
        {problem && (
          <p role="alert" className={ui.error}>
            {problem}
          </p>
        )}
      </div>
    </main>
  );
}
